import { useEffect, useState } from "react";
import api from "@/lib/api";
import { FeeStatusBadge } from "@/lib/fees";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export default function StudentFees() {
    const [data, setData] = useState(null);

    useEffect(() => {
        // /fees/me is the only fee route a student can reach without an id — and
        // /fees/students/{id} refuses any id but their own, so there's no way to
        // read anyone else's fee status. The response itself carries no amount
        // or balance field — just a percentage and dates — enforced server-side.
        api.get("/fees/me").then(r => setData(r.data)).catch(() => setData({ fee_structures: [] }));
    }, []);

    if (!data) return <div className="text-muted-foreground">Loading…</div>;

    const { fee_structures } = data;

    return (
        <div className="space-y-6" data-testid="student-fees-page">
            <div>
                <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Record-keeping only</div>
                <h1 className="font-display text-3xl lg:text-4xl mt-1">My fees</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Payments are made outside the portal (UPI/bank/cash/cheque) and logged here by admin. This page is read-only.
                </p>
            </div>

            {fee_structures.length === 0 ? (
                <div className="mx-card p-12 text-center text-muted-foreground">No fee record has been set up for you yet.</div>
            ) : (
                fee_structures.map((s, idx) => (
                    <Card key={idx} className="mx-card overflow-hidden" data-testid={`fee-card-${idx}`}>
                        <CardContent className="p-6 space-y-5">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{s.label || "Fee record"}</div>
                                {s.next_due_date && (
                                    <div className="text-xs text-muted-foreground bg-muted/50 rounded-xl px-3 py-1.5">
                                        Next due: <strong className="text-foreground">{s.next_due_date}</strong>
                                    </div>
                                )}
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Paid so far</div>
                                    <div className="font-display text-2xl">{s.percent_paid}%</div>
                                </div>
                                <Progress value={s.percent_paid} className="h-2.5" />
                            </div>

                            {s.installments.length > 0 && (
                                <div>
                                    <div className="text-sm font-semibold mb-2">Installment schedule</div>
                                    <div className="overflow-x-auto border border-border rounded-xl">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Due date</TableHead>
                                                    <TableHead>Status</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {s.installments.map((i, iidx) => (
                                                    <TableRow key={iidx}>
                                                        <TableCell className="text-sm">{i.due_date}</TableCell>
                                                        <TableCell><FeeStatusBadge status={i.status} /></TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                ))
            )}
        </div>
    );
}
